import { Building2, Cpu, Wrench, Languages, Brush } from 'lucide-react'
import { useMemo, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { filterSettingsNavItemsForAccess } from './settingsNavItems'
import { Separator } from '@/components/ui/separator'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { SidebarNav } from './components/sidebar-nav'

type SettingsNavItemConfig = {
  title: string
  href: string
  icon: JSX.Element
  /** Hidden for view-only users. */
  requiresWriteAccess: boolean
}

export function Settings() {
  const { t } = useTranslation('common')
  const canWriteOrgData = useCanWriteOrgData()

  const sidebarNavItems = useMemo(() => {
    const allSidebarNavItems: SettingsNavItemConfig[] = [
      {
        title: t('settings.account'),
        href: '/settings/account',
        icon: <Wrench size={18} />,
        requiresWriteAccess: false,
      },
      {
        title: t('settings.organization'),
        href: '/settings/organization',
        icon: <Building2 size={18} />,
        requiresWriteAccess: true,
      },
      {
        title: t('settings.llm'),
        href: '/settings/llm',
        icon: <Cpu size={18} />,
        requiresWriteAccess: true,
      },
      {
        title: t('settings.theme'),
        href: '/settings/theme',
        icon: <Brush size={18} />,
        requiresWriteAccess: false,
      },
      {
        title: t('settings.language'),
        href: '/settings/language',
        icon: <Languages size={18} />,
        requiresWriteAccess: false,
      },
    ]

    return filterSettingsNavItemsForAccess(allSidebarNavItems, canWriteOrgData)
  }, [canWriteOrgData, t])

  return (
    <>
      {/* ===== Top Heading ===== */}
      <Header>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed>
        <div className='space-y-0.5'>
          <h1 className='text-2xl font-bold tracking-tight md:text-3xl'>
            {t('settings.title')}
          </h1>
          <p className='text-muted-foreground'>{t('settings.description')}</p>
        </div>
        <Separator className='my-4 lg:my-6' />
        <div className='flex flex-1 flex-col space-y-2 overflow-hidden md:space-y-2 lg:flex-row lg:space-y-0 lg:space-x-12'>
          <aside className='top-0 lg:sticky lg:w-1/5'>
            <SidebarNav items={sidebarNavItems} />
          </aside>
          <div className='flex w-full overflow-y-hidden p-1'>
            <Outlet />
          </div>
        </div>
      </Main>
    </>
  )
}
